import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Field, NativeSelect, LoadingBlock, EmptyState, Spinner } from "@/components/Shared";

const EMPTY = { name: "", status: "active", contact_name: "", contact_email: "", contact_phone: "", notes: "" };

export function ClientDialog({ open, onClose, client, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(client ? { ...EMPTY, ...client } : EMPTY); }, [open, client]);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = Object.fromEntries(Object.keys(EMPTY).map((k) => [k, f[k] ?? ""]));
      const { data } = client ? await api.put(`/clients/${client.id}`, body) : await api.post("/clients", body);
      toast.success(client ? "Client updated" : "Client added");
      onSaved(data);
      onClose();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const inp = (k, props = {}) => <input className="field-input" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`client-${k}-input`} {...props} />;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-white" data-testid="client-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{client ? "Edit client" : "Add client"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <Field label="Client name" required>{inp("name", { required: true })}</Field>
          <Field label="Status">
            <NativeSelect value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="client-status-input">
              <option value="active">Active</option><option value="inactive">Inactive</option>
            </NativeSelect>
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Contact name">{inp("contact_name")}</Field>
            <Field label="Contact phone">{inp("contact_phone", { type: "tel" })}</Field>
          </div>
          <Field label="Contact email">{inp("contact_email", { type: "email" })}</Field>
          <Field label="Notes"><textarea rows={3} className="field-area" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="client-notes-input" /></Field>
          <button className="btn-brand h-12 w-full" disabled={busy} data-testid="client-save-button">{busy && <Spinner className="text-white" />}Save client</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Clients() {
  const [list, setList] = useState(null);
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/clients").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  return (
    <div className="fade-up">
      <PageHeader eyebrow="Customers" title="Clients" actions={
        <button className="btn-brand h-11" onClick={() => { setEdit(null); setOpen(true); }} data-testid="add-client-button"><Plus className="h-4 w-4" />Add client</button>} />
      {!list ? <LoadingBlock /> : !list.length ? <EmptyState title="No clients yet" testId="clients-empty" /> : (
        <div className="panel overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm" data-testid="clients-table">
            <thead><tr className="border-b border-border bg-slate-50 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3">Client</th><th className="px-4 py-3">Contact</th><th className="px-4 py-3 text-right">Projects</th><th className="px-4 py-3 text-right">Reports</th><th className="px-4 py-3">Status</th><th className="px-4 py-3" />
            </tr></thead>
            <tbody>
              {list.map((c) => (
                <tr key={c.id} className="border-b border-border last:border-0 hover:bg-[#5C241E]/[0.03]">
                  <td className="px-4 py-3 font-semibold"><Link to={`/admin/clients/${c.id}`} className="hover:text-[#5C241E] hover:underline" data-testid={`client-link-${c.id}`}>{c.name}</Link></td>
                  <td className="px-4 py-3 text-slate-600">{c.contact_name || "—"}<span className="block text-xs">{c.contact_email}</span></td>
                  <td className="px-4 py-3 text-right font-mono-num">{c.project_count}</td>
                  <td className="px-4 py-3 text-right font-mono-num">{c.report_count}</td>
                  <td className="px-4 py-3"><span className={`text-xs font-bold uppercase ${c.status === "active" ? "text-emerald-700" : "text-slate-500"}`}>{c.status}</span></td>
                  <td className="px-4 py-3 text-right"><button className="btn-ghost h-9 px-3 text-xs" onClick={() => { setEdit(c); setOpen(true); }} data-testid={`client-edit-${c.id}`}><Pencil className="h-3.5 w-3.5" />Edit</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ClientDialog open={open} onClose={() => setOpen(false)} client={edit} onSaved={load} />
    </div>
  );
}
